import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import ServiceDataService from '../../services/service.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const ServiceList = ({ onEdit, onCreate }) => {
  const [services, setServices] = useState([]);
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusDialog, setStatusDialog] = useState({ open: false, service: null });

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm, statusFilter]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await ServiceDataService.getAllWithInactive();
      if (response.data.success) {
        setServices(response.data.data);
      } else {
        setServices(response.data || []);
      }
    } catch (error) {
      setError('Failed to load services');
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = services;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.id.toString().includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(service => {
        if (statusFilter === 'active') return service.active !== false;
        if (statusFilter === 'inactive') return service.active === false;
        return true;
      });
    }

    setFilteredServices(filtered);
  };

  const handleStatusChange = async () => {
    try {
      const newStatus = statusDialog.service.status === 'active' ? 'inactive' : 'active';
      await ServiceDataService.update(statusDialog.service.id, { 
        name: statusDialog.service.name,
        status: newStatus 
      });
      await loadServices();
      setStatusDialog({ open: false, service: null });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setError(`Failed to ${statusDialog.service.status === 'active' ? 'deactivate' : 'activate'} service: ${errorMessage}`);
      console.error('Error changing service status:', error);
      console.error('Full error response:', error.response);
    }
  };

  const formatCurrency = (amount) => {
    const { formatCurrencySync } = require('../../utils/currencyUtils');
    return formatCurrencySync(amount);
  };

  const getStatusChip = (service) => {
    if (service.status === 'inactive') {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
          {t ('services.servicemanagment')}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
        {t ('services.msgsmanagment')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t ('services.searchsv')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t ('services.tstatus')}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('services.allstatus')}</MenuItem>
                  <MenuItem value="active">{t ('services.tactive')}</MenuItem>
                  <MenuItem value="inactive">{t ('services.tinactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  sx={{ borderRadius: 2 }}
                >
                 {t ('services.clearfilters')}
                </Button>
                {hasPermission('services.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onCreate}
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    }
                  }}
                >
                 { t ('services.addService')}
                </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>{t ('services.title')}</strong></TableCell>
                <TableCell><strong>{t ('services.tstatus')}</strong></TableCell>
                <TableCell><strong>{t ('clients.actionsfield')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BusinessIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                      {t ('services.noServicesFound')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchTerm || statusFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : t ('services.createFirstService')}
                      </Typography>
                      {!searchTerm && statusFilter === 'all' && hasPermission('services.create') && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={onCreate}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                          }}
                        >
                         { t ('services.addService')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredServices.map((service) => (
                  <TableRow key={service.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {service.name || 'Unnamed Service'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {service.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(service)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {hasPermission('services.edit') && (
                        <Tooltip title="Edit Service">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(service)}
                            sx={{ color: 'warning.main' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        )}
                        {hasPermission('services.edit') && (
                        <Tooltip title={service.status === 'active' ? 'Deactivate Service' : 'Activate Service'}>
                          <IconButton
                            size="small"
                            onClick={() => setStatusDialog({ open: true, service })}
                            sx={{ 
                              color: service.status === 'active' ? 'warning.main' : 'success.main' 
                            }}
                          >
                            {service.status === 'active' ? <DeleteIcon /> : <AddIcon />}
                          </IconButton>
                        </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, service: null })}>
        <DialogTitle>
          {statusDialog.service?.status === 'active' ? 'Deactivate Service' : 'Activate Service'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {statusDialog.service?.status === 'active' ? 'deactivate' : 'activate'} the service "{statusDialog.service?.name}"?
            {statusDialog.service?.status === 'active' 
              ? ' This service will no longer appear in client forms.' 
              : ' This service will be available for client forms.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, service: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleStatusChange}
            color={statusDialog.service?.status === 'active' ? 'warning' : 'success'}
            variant="contained"
            sx={{
              background: statusDialog.service?.status === 'active' 
                ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              '&:hover': {
                background: statusDialog.service?.status === 'active'
                  ? 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)'
                  : 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
              }
            }}
          >
            {statusDialog.service?.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceList;
